import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Star, DollarSign, Users, Shield, Video, 
  BadgeCheck, BarChart3, Loader2, Check, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  { icon: DollarSign, title: 'Monetize Content', description: 'Earn from your videos and premium subscriptions' },
  { icon: BadgeCheck, title: 'Verified Badge', description: 'Stand out with an official verified creator badge' },
  { icon: BarChart3, title: 'Advanced Analytics', description: 'Track views, engagement, and earnings in detail' },
  { icon: Users, title: 'Build Your Audience', description: 'Connect with millions of users on our platform' },
  { icon: Shield, title: 'Priority Support', description: '24/7 dedicated support for all your needs' },
  { icon: Video, title: 'Unlimited Uploads', description: 'Upload as many videos as you want in HD quality' },
];

const countries = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 
  'France', 'Spain', 'Italy', 'Brazil', 'Mexico', 'Japan', 'Other'
];

const BecomeModel = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    stage_name: '',
    email: '',
    age: '',
    country: '',
    experience: '',
    social_media: '',
    why_join: '',
    agree_terms: false
  });
  const [errors, setErrors] = useState({});

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.full_name) newErrors.full_name = 'Required';
    if (!formData.stage_name) newErrors.stage_name = 'Required';
    if (!formData.email) newErrors.email = 'Required';
    if (!formData.age || parseInt(formData.age) < 18) newErrors.age = 'Must be 18 or older';
    if (!formData.country) newErrors.country = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.experience) newErrors.experience = 'Required';
    if (!formData.why_join) newErrors.why_join = 'Required';
    if (!formData.agree_terms) newErrors.agree_terms = 'You must agree to the terms';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/apply-model`, {
        ...formData,
        age: parseInt(formData.age)
      });
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center px-4" data-testid="application-success">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h2 
            className="text-3xl font-normal tracking-wide uppercase mb-4"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Application Submitted!
          </h2>
          <p className="text-gray-400 mb-6">
            Thank you for applying to become a VelvetStream model. We'll review your application and contact you within 48 hours at <strong className="text-white">{formData.email}</strong>.
          </p>
          <Button 
            onClick={() => navigate('/')}
            className="bg-white text-black hover:bg-gray-200"
          >
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-12" data-testid="become-model-page">
      <div className="max-w-5xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Star className="w-10 h-10 text-amber-500" />
            <h1 
              className="text-4xl md:text-6xl font-normal tracking-wide uppercase"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Become a Paid Model
            </h1>
          </div>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Join our exclusive community of verified models. Monetize your content, build your brand, and connect with millions.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
          {benefits.map((benefit, i) => (
            <div key={i} className="bg-[#121212] border border-white/10 rounded-lg p-4 text-center">
              <benefit.icon className="w-8 h-8 text-amber-500 mx-auto mb-3" />
              <h3 className="text-white font-medium mb-1">{benefit.title}</h3>
              <p className="text-gray-500 text-sm">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* Application Form */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6 md:p-10">
          <h2 
            className="text-2xl font-normal tracking-wide uppercase mb-6 text-center"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Model Application
          </h2>

          {/* Progress */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-white' : 'text-gray-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-amber-500 text-black' : 'bg-gray-800'}`}>
                1
              </div>
              <span className="hidden sm:inline">Personal Info</span>
            </div>
            <div className={`w-16 h-0.5 ${step >= 2 ? 'bg-amber-500' : 'bg-gray-800'}`} />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-white' : 'text-gray-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-amber-500 text-black' : 'bg-gray-800'}`}>
                2
              </div>
              <span className="hidden sm:inline">Experience</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {step === 1 ? (
              <div className="space-y-4 max-w-lg mx-auto">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Legal Full Name *</label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    className={`bg-[#1A1A1A] border-white/10 ${errors.full_name ? 'border-red-500' : ''}`}
                    data-testid="model-full-name"
                  />
                  {errors.full_name && <p className="text-red-400 text-xs mt-1">{errors.full_name}</p>}
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Stage Name / Display Name *</label>
                  <Input
                    value={formData.stage_name}
                    onChange={(e) => setFormData({...formData, stage_name: e.target.value})}
                    className={`bg-[#1A1A1A] border-white/10 ${errors.stage_name ? 'border-red-500' : ''}`}
                    data-testid="model-stage-name"
                  />
                  {errors.stage_name && <p className="text-red-400 text-xs mt-1">{errors.stage_name}</p>}
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Email Address *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className={`bg-[#1A1A1A] border-white/10 ${errors.email ? 'border-red-500' : ''}`}
                    data-testid="model-email"
                  />
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Age *</label>
                    <Input
                      type="number"
                      min="18"
                      value={formData.age}
                      onChange={(e) => setFormData({...formData, age: e.target.value})}
                      className={`bg-[#1A1A1A] border-white/10 ${errors.age ? 'border-red-500' : ''}`}
                      data-testid="model-age"
                    />
                    {errors.age && <p className="text-red-400 text-xs mt-1">{errors.age}</p>}
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Country *</label>
                    <Select value={formData.country} onValueChange={(v) => setFormData({...formData, country: v})}>
                      <SelectTrigger className={`bg-[#1A1A1A] border-white/10 h-10 ${errors.country ? 'border-red-500' : ''}`} data-testid="model-country">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10 z-[200]">
                        {countries.map(c => (
                          <SelectItem key={c} value={c} className="text-white hover:bg-white/10 cursor-pointer">{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.country && <p className="text-red-400 text-xs mt-1">{errors.country}</p>}
                  </div>
                </div>

                <Button 
                  type="button"
                  onClick={handleNext}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black h-12 mt-4"
                  data-testid="model-next-btn"
                >
                  Continue
                </Button>
              </div>
            ) : (
              <div className="space-y-4 max-w-lg mx-auto">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Previous Experience *</label>
                  <Textarea
                    value={formData.experience}
                    onChange={(e) => setFormData({...formData, experience: e.target.value})}
                    className={`bg-[#1A1A1A] border-white/10 min-h-[100px] ${errors.experience ? 'border-red-500' : ''}`}
                    placeholder="Tell us about your experience in adult entertainment or content creation..."
                    data-testid="model-experience"
                  />
                  {errors.experience && <p className="text-red-400 text-xs mt-1">{errors.experience}</p>}
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Social Media Links (optional)</label>
                  <Input
                    value={formData.social_media}
                    onChange={(e) => setFormData({...formData, social_media: e.target.value})}
                    className="bg-[#1A1A1A] border-white/10"
                    placeholder="Twitter, Instagram, OnlyFans, etc."
                    data-testid="model-social"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Why do you want to join VelvetStream? *</label>
                  <Textarea
                    value={formData.why_join}
                    onChange={(e) => setFormData({...formData, why_join: e.target.value})}
                    className={`bg-[#1A1A1A] border-white/10 min-h-[100px] ${errors.why_join ? 'border-red-500' : ''}`}
                    placeholder="What are your goals and what do you hope to achieve on our platform?"
                    data-testid="model-why-join"
                  />
                  {errors.why_join && <p className="text-red-400 text-xs mt-1">{errors.why_join}</p>}
                </div>

                <label className={`flex items-start gap-3 cursor-pointer p-3 rounded ${errors.agree_terms ? 'bg-red-500/10' : ''}`}>
                  <input
                    type="checkbox"
                    checked={formData.agree_terms}
                    onChange={(e) => setFormData({...formData, agree_terms: e.target.checked})}
                    className="w-4 h-4 mt-0.5"
                    data-testid="model-terms"
                  />
                  <span className="text-gray-300 text-sm">
                    I confirm that I am at least 18 years old and agree to the Terms of Service and Model Agreement.
                  </span>
                </label>
                {errors.agree_terms && (
                  <p className="text-red-400 text-xs flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {errors.agree_terms}
                  </p>
                )}

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 border-white/20"
                  >
                    Back
                  </Button>
                  <Button 
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
                    data-testid="model-submit-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Application'}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default BecomeModel;
